import { MutableRefObject, useContext, useEffect, useRef } from "react";

import { useStore } from "zustand";

import { CaseBlockScrollContext } from "src/cases/caseView/CaseBlockScrollContext";

const useCaseBlockScrollStore = () => {
  const store = useContext(CaseBlockScrollContext);
  if (!store) {
    throw new Error("CaseBlockScrollContext is not defined");
  }
  return store;
};

export const useCaseBlockScrollActions = () => {
  const store = useCaseBlockScrollStore();
  return useStore(store, (state) => state.actions);
};

export const useCaseBlockScrollTarget = <T extends HTMLElement>(
  blockId: string,
  { onScrollEnd }: { onScrollEnd?: () => void } = {},
) => {
  const { registerBlock, unregisterBlock, setBlockVisible } =
    useCaseBlockScrollActions();
  const ref = useRef<T | null>(null);
  const onScrollEndRef = useRef(onScrollEnd);

  useEffect(() => {
    onScrollEndRef.current = onScrollEnd;
  }, [onScrollEnd]);

  useEffect(() => {
    if (ref.current && blockId) {
      registerBlock(
        blockId,
        ref as MutableRefObject<HTMLElement | null>,
        onScrollEndRef,
      );

      // Set up intersection observer for this block
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          const rect = entry.boundingClientRect;
          const position = {
            top: rect.top,
            left: rect.left,
          };
          setBlockVisible(blockId, entry.isIntersecting, position);
        },
        { threshold: 0.5 },
      );

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
        unregisterBlock(blockId);
      };
    }
    return () => {
      if (blockId) {
        unregisterBlock(blockId);
      }
    };
  }, [blockId, registerBlock, unregisterBlock, setBlockVisible]);

  return { ref, onScrollEndRef };
};

export const useCaseBlockScrollTo = () => {
  const { scrollToBlock } = useCaseBlockScrollActions();
  return scrollToBlock;
};

const getTopmostVisibleBlock = (
  visibleBlocks: Map<string, { top: number; left: number }>,
): string | null => {
  if (visibleBlocks.size === 0) {
    return null;
  }

  let topmostVisibleBlock: string | null = null;
  let topmostPosition = { top: Infinity, left: Infinity };

  for (const [blockId, blockPosition] of visibleBlocks) {
    if (
      blockPosition.top < topmostPosition.top ||
      (blockPosition.top === topmostPosition.top &&
        blockPosition.left < topmostPosition.left)
    ) {
      topmostVisibleBlock = blockId;
      topmostPosition = blockPosition;
    }
  }

  return topmostVisibleBlock;
};

export const useActiveBlock = () => {
  const store = useCaseBlockScrollStore();
  return useStore(store, (state) => {
    return (
      state.lastFocusedBlock || getTopmostVisibleBlock(state.visibleBlocks)
    );
  });
};

export const useLastFocusedBlock = () => {
  const store = useCaseBlockScrollStore();
  return useStore(store, (state) => state.lastFocusedBlock);
};

export const useUnsetLastFocusedBlock = () => {
  const { unsetLastFocusedBlock } = useCaseBlockScrollActions();
  return unsetLastFocusedBlock;
};
